import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { requestsApi } from '../services/api';
import Navbar from '../components/Navbar';

const Results = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterRequestId = searchParams.get('request_id') || '';

  const [results, setResults] = useState({ data: [], page: 1, pages: 1, total: 0 });
  const [requests, setRequests] = useState([]);
  const [error, setError] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    requestsApi.getAll().then((res) => setRequests(res.data)).catch(() => {});
  }, []);

  const loadResults = useCallback(async (page, requestId) => {
    try {
      setLoading(true);
      const res = await requestsApi.getAllResults(page, requestId || undefined);
      setResults(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResults(1, filterRequestId);
  }, [loadResults, filterRequestId]);

  const handleFilterChange = (e) => {
    const val = e.target.value;
    if (val) {
      setSearchParams({ request_id: val });
    } else {
      setSearchParams({});
    }
  };

  const handleRead = async (result) => {
    setSelectedResult(result);
    if (!result.is_read) {
      try {
        await requestsApi.markResultRead(result.id);
        setResults((prev) => ({
          ...prev,
          data: prev.data.map((r) =>
            r.id === result.id ? { ...r, is_read: 1 } : r
          ),
        }));
      } catch (err) {
        // silently fail marking read
      }
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await requestsApi.deleteResult(id);
      if (results.data.length === 1 && results.page > 1) {
        loadResults(results.page - 1, filterRequestId);
      } else {
        loadResults(results.page, filterRequestId);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete result');
    } finally {
      setDeletingId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await requestsApi.markAllRead(filterRequestId || undefined);
      setResults((prev) => ({
        ...prev,
        data: prev.data.map((r) => ({ ...r, is_read: 1 })),
      }));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark all as read');
    }
  };

  const handlePageChange = (page) => {
    loadResults(page, filterRequestId);
  };

  const pageNumbers = [];
  for (let i = 1; i <= results.pages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Results</h2>
          <div className="flex items-center gap-2">
            {results.data.some((r) => !r.is_read) && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm px-3 py-1.5 border border-gray-300 text-gray-600 rounded-md hover:bg-gray-100 transition"
              >
                Mark all as read
              </button>
            )}
            <select
              value={filterRequestId}
              onChange={handleFilterChange}
              className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Requests</option>
              {requests.map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <div
            className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 cursor-pointer text-sm"
            onClick={() => setError('')}
          >
            {error}
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        )}

        {!loading && results.data.length === 0 && !error && (
          <div className="text-center py-12 text-gray-500">
            <p>No results yet. Results will appear here after your scheduled research runs.</p>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {results.data.map((r) => (
            <div
              key={r.id}
              className={`bg-white border rounded-lg p-4 ${
                r.is_read ? 'border-gray-200' : 'border-primary-300 bg-primary-50/30'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm text-gray-900">
                      {r.request_name}
                    </span>
                    {!r.is_read && (
                      <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-100 text-primary-700">
                        Unread
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {new Date(r.run_time).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {r.response_text}
                  </p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleRead(r)}
                    className="text-sm px-3 py-1 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-500 hover:text-white transition"
                  >
                    Read
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    disabled={deletingId === r.id}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition disabled:opacity-60 disabled:cursor-not-allowed"
                    title="Delete"
                  >
                    {deletingId === r.id ? (
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        <line x1="10" y1="11" x2="10" y2="17" />
                        <line x1="14" y1="11" x2="14" y2="17" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {results.pages > 1 && (
          <div className="flex items-center justify-center gap-1 mt-6">
            <button
              onClick={() => handlePageChange(results.page - 1)}
              disabled={results.page <= 1}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>
            {pageNumbers.map((p) => (
              <button
                key={p}
                onClick={() => handlePageChange(p)}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  p === results.page
                    ? 'bg-primary-600 text-white'
                    : 'border border-gray-300 hover:bg-gray-100'
                }`}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(results.page + 1)}
              disabled={results.page >= results.pages}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-100 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modal */}
      {selectedResult && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setSelectedResult(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-900">{selectedResult.request_name}</h3>
                <span className="text-xs text-gray-400">
                  {new Date(selectedResult.run_time).toLocaleString()}
                </span>
              </div>
              <button
                onClick={() => setSelectedResult(null)}
                className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="px-6 py-4 overflow-y-auto">
              <pre className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed font-sans">
                {selectedResult.response_text}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Results;
